import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import StatusBadge from "@/components/shared/StatusBadge";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { Battery, Signal, Search, Filter, Cpu, Car, ChevronDown, X, Zap, Activity, RefreshCw, MoreVertical, Eye, Power, Plus, Loader2, Copy, KeyRound } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function formatDateTime(value) {
  if (!value) return "Never seen";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never seen";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSignalLabel(signalBars) {
  if (signalBars >= 4) return "Exc";
  if (signalBars === 3) return "Good";
  if (signalBars === 2) return "Fair";
  if (signalBars === 1) return "Poor";
  return "None";
}

function getDeviceIdentity(device) {
  return device?.device_id;
}

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export default function DevicesPage() {
  const { can } = useAuth();
  const canCreateDevices = can("DEVICES.CREATE");
  const canActivateDevices = can("DEVICES.ACTIVATE");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [batteryFilter, setBatteryFilter] = useState("all");
  const [firmwareFilter, setFirmwareFilter] = useState("all");
  const [signalFilter, setSignalFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [devices, setDevices] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [deviceDetail, setDeviceDetail] = useState(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [provisionResultOpen, setProvisionResultOpen] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);
  const [createForm, setCreateForm] = useState({
    mac_address: "",
    organization_id: "",
    vehicle_id: "",
    firmware_version: "",
  });
  const missingDeviceIdWarningsRef = useRef(new Set());

  const selectedProvisionVehicle = useMemo(
    () => vehicles.find((vehicle) => String(vehicle.id) === String(createForm.vehicle_id)) || null,
    [vehicles, createForm.vehicle_id],
  );

  useEffect(() => {
    if (selectedProvisionVehicle?.organization_id) {
      setCreateForm((prev) => ({
        ...prev,
        organization_id: String(selectedProvisionVehicle.organization_id),
      }));
    }
  }, [selectedProvisionVehicle?.organization_id]);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (vehicleFilter !== "all") params.vehicle_id = vehicleFilter;
      if (activeFilter !== "all") params.is_active = activeFilter === "true";

      const { data } = await api.get("/devices", { params });
      setDevices(Array.isArray(data?.data) ? data.data : []);
      setMeta(
        data?.meta || {
          total: 0,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setDevices([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [vehicleFilter, activeFilter, page, pageSize]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const fetchDeviceDetail = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    try {
      const response = await api.get(`/devices/${deviceId}`);
      setDeviceDetail(unwrapSingle(response));
      setSelectedDeviceId(deviceId);
    } catch (err) {
      const { message } = extractApiError(err);
      setDetailError(message || "Failed to load device details.");
      setDeviceDetail(null);
      setSelectedDeviceId(deviceId);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const fetchCreateLookups = useCallback(async () => {
    setLoadingLookups(true);
    try {
      const [organizationsResponse, vehiclesResponse] = await Promise.all([
        api.get("/organizations", { params: { is_active: true, page: 1, page_size: 200 } }),
        api.get("/vehicles", { params: { is_active: true, page: 1, page_size: 200 } }),
      ]);
      setOrganizations(normalizeCollection(organizationsResponse?.data));
      setVehicles(normalizeCollection(vehiclesResponse?.data));
    } catch {
      setOrganizations([]);
      setVehicles([]);
    } finally {
      setLoadingLookups(false);
    }
  }, []);

  useEffect(() => {
    if (!createOpen) return;
    fetchCreateLookups();
  }, [createOpen, fetchCreateLookups]);

  const filteredDevices = useMemo(() => {
    const cleanQ = q.trim().toLowerCase().replace(/\s+/g, "");

    return devices.filter((device) => {
      const deviceId = (device.device_id || "").toLowerCase();
      const macAddress = (device.mac_address || "").toLowerCase();
      const registration = (device.vehicle_registration_number || "").toLowerCase().replace(/\s+/g, "");
      const firmwareVersion = device.firmware_version || "0";
      const status = device.status || "offline";
      const battery = Number(device.device_battery_pct || 0);
      const hasBatteryValue = battery > 0;
      const signalBars = Number(device.signal_bars || 0);

      const matchesSearch =
        !cleanQ ||
        deviceId.includes(cleanQ) ||
        macAddress.includes(cleanQ) ||
        registration.includes(cleanQ);

      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesFirmware = firmwareFilter === "all" || firmwareVersion === firmwareFilter;

      let matchesBattery = true;
      if (batteryFilter === "high") matchesBattery = hasBatteryValue && battery >= 80;
      else if (batteryFilter === "medium") matchesBattery = hasBatteryValue && battery >= 20 && battery < 80;
      else if (batteryFilter === "low") matchesBattery = hasBatteryValue && battery < 20;

      const matchesSignal = signalFilter === "all" || signalBars === Number(signalFilter);

      return matchesSearch && matchesStatus && matchesFirmware && matchesBattery && matchesSignal;
    });
  }, [devices, q, statusFilter, firmwareFilter, batteryFilter, signalFilter]);

  const uniqueFirmwares = useMemo(() => {
    const firmwares = [...new Set(devices.map((device) => device.firmware_version).filter(Boolean))];
    return firmwares.sort((left, right) => String(left).localeCompare(String(right), undefined, { numeric: true }));
  }, [devices]);

  const vehicleOptions = useMemo(() => {
    const vehicleMap = new Map();

    devices.forEach((device) => {
      if (!device.vehicle_id || vehicleMap.has(device.vehicle_id)) return;
      vehicleMap.set(device.vehicle_id, {
        vehicle_id: device.vehicle_id,
        registration: device.vehicle_registration_number || device.vehicle_id,
      });
    });

    return Array.from(vehicleMap.values()).sort((left, right) => left.registration.localeCompare(right.registration));
  }, [devices]);

  const resetFilters = () => {
    setQ("");
    setStatusFilter("all");
    setBatteryFilter("all");
    setFirmwareFilter("all");
    setSignalFilter("all");
    setVehicleFilter("all");
    setActiveFilter("all");
    setPage(1);
  };

  const openStatusToggleDialog = (device) => {
    setStatusTarget(device);
    setStatusDialogOpen(true);
  };

  const handleToggleStatus = async () => {
    if (!canActivateDevices) {
      toast.error("You do not have permission to activate/deactivate devices.");
      return;
    }

    if (!statusTarget) return;
    const deviceId = getDeviceIdentity(statusTarget);
    if (!deviceId) return;

    setStatusSubmitting(true);
    try {
      await api.patch(`/devices/${deviceId}/status`, { is_active: !statusTarget.is_active });
      toast.success(`Device ${statusTarget.is_active ? "deactivated" : "activated"} successfully.`);
      setStatusDialogOpen(false);
      setStatusTarget(null);
      await fetchDevices();
      if (detailOpen && selectedDeviceId && String(selectedDeviceId) === String(deviceId)) {
        await fetchDeviceDetail(deviceId);
      }
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to update device status.");
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleCreateDevice = async (event) => {
    event.preventDefault();
    setCreateError("");

    if (!createForm.mac_address.trim()) {
      setCreateError("MAC address is required.");
      return;
    }
    if (!createForm.organization_id) {
      setCreateError("Organization is required.");
      return;
    }
    if (!createForm.vehicle_id) {
      setCreateError("Vehicle is required.");
      return;
    }

    const selectedVehicle = vehicles.find((vehicle) => String(vehicle.id) === String(createForm.vehicle_id));
    if (!selectedVehicle) {
      setCreateError("Please select a valid vehicle.");
      return;
    }
    if (!selectedVehicle.vin?.trim()) {
      setCreateError("Selected vehicle must have a VIN.");
      return;
    }
    if (String(selectedVehicle.organization_id) !== String(createForm.organization_id)) {
      setCreateError("Selected vehicle must belong to the chosen organization.");
      return;
    }

    const payload = {
      mac_address: createForm.mac_address.trim(),
      organization_id: createForm.organization_id,
      vehicle_id: createForm.vehicle_id,
      ...(createForm.firmware_version.trim() ? { firmware_version: createForm.firmware_version.trim() } : {}),
    };

    setCreateSubmitting(true);
    try {
      const response = await api.post("/devices", payload);
      const created = unwrapSingle(response) ?? undefined;

      setProvisionResult(created || null);
      setProvisionResultOpen(true);
      setCreateOpen(false);
      setCreateForm({
        mac_address: "",
        organization_id: "",
        vehicle_id: "",
        firmware_version: "",
      });

      toast.success("Device provisioned successfully.");
      await fetchDevices();
    } catch (err) {
      const { message } = extractApiError(err);
      setCreateError(message || "Failed to provision device.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(`${label} copied.`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const activeFiltersCount = [
    statusFilter !== "all",
    batteryFilter !== "all",
    firmwareFilter !== "all",
    signalFilter !== "all",
    vehicleFilter !== "all",
    activeFilter !== "all",
  ].filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="devices-page">
      <PageHeader
        title="IoT Devices"
        subtitle="SafetyHub fleet — telemetry, firmware and connectivity."
        actions={
          canCreateDevices ? (
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setCreateOpen(true)}>
              <Plus size={14} className="mr-1.5" /> Provision Device
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={activeFilter === "all" ? "default" : "outline"}
          className={activeFilter === "all" ? "bg-slate-900 text-white" : "border-slate-200"}
          onClick={() => {
            setActiveFilter("all");
            setPage(1);
          }}
        >
          All
        </Button>
        <Button
          variant={activeFilter === "true" ? "default" : "outline"}
          className={activeFilter === "true" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border-slate-200"}
          onClick={() => {
            setActiveFilter("true");
            setPage(1);
          }}
        >
          Online / Active
        </Button>
        <Button
          variant={activeFilter === "false" ? "default" : "outline"}
          className={activeFilter === "false" ? "bg-slate-700 text-white hover:bg-slate-800" : "border-slate-200"}
          onClick={() => {
            setActiveFilter("false");
            setPage(1);
          }}
        >
          Offline / Deactivated
        </Button>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search current page by device ID, MAC, or vehicle number..."
              className="h-11 border-slate-200 bg-slate-50/50 pl-10"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50">
                <Filter size={14} className="text-slate-400" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
                    {activeFiltersCount}
                  </span>
                )}
                <ChevronDown size={14} className="text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-4" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <h4 className="text-sm font-bold text-slate-900">Device Filters</h4>
                  {activeFiltersCount > 0 && (
                    <button onClick={resetFilters} className="text-[11px] font-bold text-blue-600 hover:underline">
                      Reset All
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Vehicle</label>
                    <Select
                      value={vehicleFilter}
                      onValueChange={(value) => {
                        setVehicleFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                          <Car size={13} className="text-slate-400" />
                          <SelectValue placeholder="All Vehicles" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Vehicles</SelectItem>
                        {vehicleOptions.map((vehicle) => (
                          <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                            {vehicle.registration}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Active State</label>
                    <Select
                      value={activeFilter}
                      onValueChange={(value) => {
                        setActiveFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                          <Activity size={13} className="text-slate-400" />
                          <SelectValue placeholder="All Devices" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Devices</SelectItem>
                        <SelectItem value="true">Active / Online</SelectItem>
                        <SelectItem value="false">Deactivated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Status</label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="offline">Offline</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Battery Level</label>
                    <Select value={batteryFilter} onValueChange={setBatteryFilter}>
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                          <Battery size={13} className="text-slate-400" />
                          <SelectValue placeholder="All Levels" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="high">High (80%+)</SelectItem>
                        <SelectItem value="medium">Medium (20-79%)</SelectItem>
                        <SelectItem value="low">Low (&lt;20%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Signal Strength</label>
                    <Select value={signalFilter} onValueChange={setSignalFilter}>
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                          <Signal size={13} className="text-slate-400" />
                          <SelectValue placeholder="All Strengths" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Strengths</SelectItem>
                        <SelectItem value="4">Excellent (4 bars)</SelectItem>
                        <SelectItem value="3">Good (3 bars)</SelectItem>
                        <SelectItem value="2">Fair (2 bars)</SelectItem>
                        <SelectItem value="1">Poor (1 bar)</SelectItem>
                        <SelectItem value="0">No Signal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Firmware</label>
                    <Select value={firmwareFilter} onValueChange={setFirmwareFilter}>
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                          <Cpu size={13} className="text-slate-400" />
                          <SelectValue placeholder="All Versions" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Versions</SelectItem>
                        {uniqueFirmwares.map((firmware) => (
                          <SelectItem key={firmware} value={firmware}>
                            v{firmware}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Page Size</label>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(value) => {
                        setPageSize(Number(value));
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10 border-slate-200 bg-slate-50/50">
                        <SelectValue placeholder="Page size" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {(q || activeFiltersCount > 0) && (
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="h-11 px-3 text-slate-400 transition-colors hover:text-rose-500"
              title="Clear all filters"
            >
              <X size={16} />
            </Button>
          )}

          <Button
            variant="outline"
            onClick={fetchDevices}
            className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50"
            title="Refresh devices"
          >
            <RefreshCw size={14} />
            Refresh
          </Button>
        </div>

        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-medium text-slate-500">
            {loading ? "Loading devices..." : `${meta.total} devices found`}
          </span>
          <span className="text-xs text-slate-500">
            Page {meta.page || page} of {meta.pages || 1}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <SectionCard padded={false}>
        <DataTable
          data={filteredDevices}
          rowKey="id"
          loading={loading}
          loadingMessage="Loading devices..."
          emptyState={
            <div className="flex flex-col items-center gap-3 px-5 py-16 text-center text-slate-500">
              <Activity size={40} className="text-slate-100" />
              <p className="font-medium">No IoT devices found matching your criteria.</p>
              <button onClick={resetFilters} className="text-sm font-bold text-blue-600 hover:underline">
                Clear all filters
              </button>
            </div>
          }
          headClassName="font-bold"
          headerCellClassName="py-4"
          defaultCellClassName="py-4"
          rowClassName="text-sm transition-colors hover:bg-slate-50/50"
          bodyClassName="divide-y divide-slate-100"
          columns={[
            {
              key: "device-id", label: "Device ID",
              cellClassName: "font-mono text-xs font-black text-slate-900",
              render: (_device, index) => `iot-${String((page - 1) * pageSize + index + 1).padStart(3, "0")}`,
            },
            {
              key: "mac-firmware", label: "MAC & Firmware",
              render: (device) => (
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">{device.mac_address || "-"}</span>
                  <span className="text-[10px] font-mono uppercase tracking-tighter text-slate-400">
                    FW Version {device.firmware_version || "0"}
                  </span>
                </div>
              ),
            },
            {
              key: "vehicle-linked", label: "Vehicle Linked",
              render: (device) => (
                <span className="font-mono text-[13px] font-bold text-slate-700">
                  {device.vehicle_registration_number || "-"}
                </span>
              ),
            },
            {
              key: "power-status", label: "Power Status",
              render: (device) => {
                const battery = Number(device.device_battery_pct || 0);
                const showDash = battery <= 0;
                return (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Battery size={16} className={showDash || battery > 20 ? "text-slate-400" : "text-rose-500"} />
                      {!showDash && (
                        <div
                          className={`absolute left-[3px] top-[5px] h-[6px] rounded-[1px] ${
                            battery > 80 ? "bg-emerald-500" : battery > 20 ? "bg-amber-500" : "bg-rose-500"
                          }`}
                          style={{ width: `${Math.max(1, (battery / 100) * 10)}px` }}
                        />
                      )}
                    </div>
                    <span className={`tabular-nums font-bold ${!showDash && battery <= 20 ? "text-rose-600" : "text-slate-900"}`}>
                      {showDash ? "-" : `${battery}%`}
                    </span>
                  </div>
                );
              },
            },
            {
              key: "connectivity", label: "Connectivity",
              render: (device) => {
                const signalBars = Number(device.signal_bars || 0);
                return (
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4].map((bar) => (
                      <div
                        key={bar}
                        className={`w-1 rounded-full transition-all duration-500 ${
                          bar <= signalBars
                            ? signalBars >= 3 ? "bg-emerald-500" : "bg-amber-500"
                            : "bg-slate-100"
                        }`}
                        style={{ height: `${bar * 3 + 4}px` }}
                      />
                    ))}
                    <span className="ml-1 text-[11px] font-black uppercase tracking-tighter text-slate-400">
                      {getSignalLabel(signalBars)}
                    </span>
                  </div>
                );
              },
            },
            {
              key: "activity", label: "Activity",
              render: (device) => (
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Zap size={12} className="text-blue-500" />
                  {formatDateTime(device.last_seen_at || device.created_at)}
                </div>
              ),
            },
            {
              key: "status", label: "Status",
              render: (device) => (
                <div className="flex flex-col gap-1.5">
                  <StatusBadge status={device.status || "offline"} />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${
                    device.is_active ? "text-emerald-600" : "text-slate-400"
                  }`}>
                    {device.is_active ? "Active" : "Deactivated"}
                  </span>
                </div>
              ),
            },
            {
              key: "actions", label: "Actions", className: "text-right", cellClassName: "text-right",
              render: (device) => {
                const missingDeviceId = !device?.device_id;
                const warnKey = String(device?.id || device?.mac_address || "unknown-device");

                if (missingDeviceId && !missingDeviceIdWarningsRef.current.has(warnKey)) {
                  missingDeviceIdWarningsRef.current.add(warnKey);
                  console.warn("[DevicesPage] Row missing device_id; management actions disabled.", {
                    id: device?.id,
                    device_id: device?.device_id,
                    mac_address: device?.mac_address,
                  });
                }

                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={missingDeviceId}
                        title={missingDeviceId ? "Device ID missing — cannot manage this device" : undefined}
                        onClick={() => fetchDeviceDetail(getDeviceIdentity(device))}
                      >
                        <Eye size={14} /> View
                      </DropdownMenuItem>
                      {canActivateDevices && (
                        <DropdownMenuItem
                          disabled={missingDeviceId}
                          title={missingDeviceId ? "Device ID missing — cannot manage this device" : undefined}
                          onClick={() => openStatusToggleDialog(device)}
                        >
                          <Power size={14} /> {device.is_active ? "Deactivate" : "Activate"}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              },
            },
          ]}
        />

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-4">
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page <= 1}
            onClick={() => setPage((previousPage) => Math.max(1, previousPage - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page >= (meta.pages || 1)}
            onClick={() => setPage((previousPage) => Math.min(meta.pages || 1, previousPage + 1))}
          >
            Next
          </Button>
        </div>
      </SectionCard>

      <Dialog open={detailOpen} onOpenChange={(open) => {
        setDetailOpen(open);
        if (!open) {
          setDeviceDetail(null);
          setSelectedDeviceId(null);
          setDetailError("");
        }
      }}>
        <DialogContent className="sm:max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle>Device Detail</DialogTitle>
            <DialogDescription>Loaded from /devices/{'{'}device_id{'}'}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-8 flex items-center justify-center text-slate-500">
              <Loader2 size={18} className="mr-2 animate-spin" /> Loading device details...
            </div>
          ) : detailError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {detailError}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Device ID" value={deviceDetail?.device_id || deviceDetail?.id || "-"} mono />
              <Field label="MAC Address" value={deviceDetail?.mac_address || "-"} mono />
              <Field label="Firmware" value={deviceDetail?.firmware_version || "0"} />
              <Field label="Vehicle" value={deviceDetail?.vehicle_registration_number || "-"} mono />
              <Field label="Status" value={deviceDetail?.status || "offline"} />
              <Field label="Active" value={deviceDetail?.is_active ? "Yes" : "No"} />
              <Field label="Signal Bars" value={String(deviceDetail?.signal_bars ?? 0)} />
              <Field
                label="Battery"
                value={Number(deviceDetail?.device_battery_pct || 0) > 0 ? `${Number(deviceDetail?.device_battery_pct || 0)}%` : "-"}
              />
              <Field label="Last Seen" value={formatDateTime(deviceDetail?.last_seen_at)} className="col-span-2" />
            </div>
          )}

          {deviceDetail && !detailLoading && !detailError && canActivateDevices && (
            <DialogFooter>
              <Button variant="outline" onClick={() => openStatusToggleDialog(deviceDetail)} className="border-slate-200">
                <Power size={14} className="mr-1.5" /> {deviceDetail.is_active ? "Deactivate" : "Activate"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (!open) setCreateError("");
      }}>
        <DialogContent className="sm:max-w-xl rounded-2xl">
          <DialogHeader>
            <DialogTitle>Provision New Device</DialogTitle>
            <DialogDescription>Create via POST /devices. EMQX credentials are shown once after success.</DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleCreateDevice}>
            <div className="space-y-1.5">
              <Label>MAC Address</Label>
              <Input
                value={createForm.mac_address}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, mac_address: event.target.value }))}
                placeholder="AA:BB:CC:DD:EE:FF"
                className="font-mono"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label>Organization</Label>
              <Select
                value={createForm.organization_id || ""}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, organization_id: value }))}
                disabled={loadingLookups}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingLookups ? "Loading organizations..." : "Select organization"} />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Vehicle (Optional)</Label>
              <Select
                value={createForm.vehicle_id || "none"}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, vehicle_id: value === "none" ? "" : value }))}
                disabled={loadingLookups}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingLookups ? "Loading vehicles..." : "Select vehicle"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.registration_number || vehicle.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Firmware Version (Optional)</Label>
              <Input
                value={createForm.firmware_version}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, firmware_version: event.target.value }))}
                placeholder="e.g. 1.0.3"
              />
            </div>

            {createError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {createError}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={createSubmitting}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" disabled={createSubmitting}>
                {createSubmitting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Provisioning...</> : "Provision Device"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={provisionResultOpen} onOpenChange={setProvisionResultOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} className="text-blue-600" /> One-time Device Credentials</DialogTitle>
            <DialogDescription>
              Save these now. EMQX credentials are only returned once at provisioning time.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <CredentialRow
              label="Device ID"
              value={provisionResult?.device_id || provisionResult?.id || "-"}
              onCopy={() => copyText(provisionResult?.device_id || provisionResult?.id || "", "Device ID")}
            />
            <CredentialRow
              label="MAC"
              value={provisionResult?.mac_address || "-"}
              onCopy={() => copyText(provisionResult?.mac_address || "", "MAC address")}
            />
            <CredentialRow
              label="EMQX Username"
              value={provisionResult?.emqx_username || "-"}
              onCopy={() => copyText(provisionResult?.emqx_username || "", "EMQX username")}
            />
            <CredentialRow
              label="EMQX Password"
              value={provisionResult?.emqx_password || "-"}
              onCopy={() => copyText(provisionResult?.emqx_password || "", "EMQX password")}
              sensitive
            />
          </div>

          <DialogFooter>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setProvisionResultOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={statusDialogOpen}
        onOpenChange={(open) => {
          if (!open && !statusSubmitting) {
            setStatusDialogOpen(false);
            setStatusTarget(null);
          }
        }}
        title={`${statusTarget?.is_active ? "Deactivate" : "Activate"} device?`}
        description={
          <>
            <span className="font-semibold text-slate-900">{statusTarget?.mac_address || statusTarget?.device_id || statusTarget?.id}</span>
            {statusTarget?.is_active
              ? " will stop sending telemetry immediately."
              : " will be allowed to send telemetry again."}
          </>
        }
        confirmLabel={statusTarget?.is_active ? "Deactivate" : "Activate"}
        variant={statusTarget?.is_active ? "danger" : "default"}
        loading={statusSubmitting}
        onConfirm={handleToggleStatus}
      />
    </div>
  );
}

function Field({ label, value, mono = false, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-1 text-sm font-semibold text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function CredentialRow({ label, value, onCopy, sensitive = false }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="mb-1 text-[11px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-semibold text-slate-900 ${sensitive ? "font-mono" : ""}`}>{value}</span>
        <Button type="button" variant="ghost" size="sm" className="h-8" onClick={onCopy}>
          <Copy size={14} className="mr-1.5" /> Copy
        </Button>
      </div>
    </div>
  );
}

